import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, Calendar, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";

interface FeaturedPackage {
  id: number;
  name: string;
  description: string;
  price: string;
  duration: string;
  image_url: string;
  includes: string[];
}

interface FeaturedAccommodation {
  id: number;
  name: string;
  type: string;
  location: string;
  rating: number;
  price_range: string;
  image_url: string;
  amenities: string[];
  description: string;
}

interface HeroFeaturedContentProps {
  isScrolled: boolean;
  onBookNow: () => void;
}

export const HeroFeaturedContent = ({ isScrolled, onBookNow }: HeroFeaturedContentProps) => {
  const [featuredPackages, setFeaturedPackages] = useState<FeaturedPackage[]>([]);
  const [featuredAccommodations, setFeaturedAccommodations] = useState<FeaturedAccommodation[]>([]);
  const [currentSection, setCurrentSection] = useState<'packages' | 'accommodations'>('packages');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedContent();
  }, []);

  const fetchFeaturedContent = async () => {
    try {
      setIsLoading(true);
      
      // Fetch featured packages
      const { data: packages } = await supabase
        .from('sns_packages')
        .select('*')
        .eq('is_featured', true)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(3);

      // Fetch featured accommodations
      const { data: accommodations } = await supabase
        .from('sns_accommodations')
        .select('*')
        .eq('is_featured', true)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(3);

      setFeaturedPackages(packages || []);
      setFeaturedAccommodations(accommodations || []);
    } catch (error) {
      console.error('Error fetching featured content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentItems = currentSection === 'packages' ? featuredPackages : featuredAccommodations;
  const maxIndex = Math.max(0, currentItems.length - 1);

  const nextItem = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, currentItems.length));
  };

  const prevItem = () => {
    setCurrentIndex((prev) => (prev - 1 + currentItems.length) % Math.max(1, currentItems.length));
  };

  const switchSection = (section: 'packages' | 'accommodations') => {
    setCurrentSection(section);
    setCurrentIndex(0);
  };

  if (isLoading || (!featuredPackages.length && !featuredAccommodations.length)) {
    return null;
  }

  return (
    <div className={cn(
      "absolute bottom-6 left-1/2 transform -translate-x-1/2 w-full max-w-4xl px-4 transition-all duration-700",
      isScrolled ? "opacity-0 translate-y-10" : "opacity-100 translate-y-0"
    )}>
      {/* Section Selector - More Compact */}
      <div className="flex justify-center mb-3">
        <div className="bg-white/10 backdrop-blur-md rounded-full p-0.5 border border-white/20">
          <Button
            variant={currentSection === 'packages' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => switchSection('packages')}
            className={cn(
              "rounded-full px-4 py-1 text-white transition-all text-sm",
              currentSection === 'packages' 
                ? "bg-white/20 shadow-lg" 
                : "hover:bg-white/10"
            )}
          >
            <Calendar className="w-3 h-3 mr-1" />
            Featured Packages
          </Button>
          <Button
            variant={currentSection === 'accommodations' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => switchSection('accommodations')}
            className={cn(
              "rounded-full px-4 py-1 text-white transition-all text-sm",
              currentSection === 'accommodations' 
                ? "bg-white/20 shadow-lg" 
                : "hover:bg-white/10"
            )}
          >
            <MapPin className="w-3 h-3 mr-1" />
            Featured Stays
          </Button>
        </div>
      </div>

      {/* Featured Content - More Compact */}
      {currentItems.length > 0 && (
        <div className="relative">
          <Card className="bg-white/10 backdrop-blur-md border-white/20 shadow-2xl">
            <CardContent className="p-4">
              {currentSection === 'packages' ? (
                <PackageCard 
                  package={featuredPackages[currentIndex]} 
                  onBookNow={onBookNow}
                />
              ) : (
                <AccommodationCard 
                  accommodation={featuredAccommodations[currentIndex]} 
                  onBookNow={onBookNow}
                />
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          {currentItems.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={prevItem}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={nextItem}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </>
          )}

          {/* Dots Indicator */}
          {currentItems.length > 1 && (
            <div className="flex justify-center mt-2 space-x-1">
              {currentItems.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    index === currentIndex 
                      ? "bg-white scale-125" 
                      : "bg-white/40 hover:bg-white/60"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main CTA - More Compact */}
      <div className="text-center mt-4">
        <Button
          onClick={onBookNow}
          size="lg"
          className="bg-travel-primary hover:bg-travel-primary/90 text-white px-6 py-2 rounded-full font-semibold shadow-2xl transition-all transform hover:scale-105"
        >
          Book Your Adventure Now
        </Button>
      </div>
    </div>
  );
};

const PackageCard = ({ package: pkg, onBookNow }: { package: FeaturedPackage; onBookNow: () => void }) => (
  <div className="grid md:grid-cols-2 gap-6 items-center">
    <div className="relative overflow-hidden rounded-lg">
      <img
        src={pkg.image_url}
        alt={pkg.name}
        className="w-full h-48 object-cover transition-transform hover:scale-105"
      />
      <Badge className="absolute top-2 left-2 bg-travel-primary text-white">
        Featured Package
      </Badge>
    </div>
    <div className="text-white">
      <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
      <p className="text-white/80 mb-4 line-clamp-2">{pkg.description}</p>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          <span className="text-sm">{pkg.duration}</span>
        </div>
        <div className="text-xl font-bold text-travel-secondary">
          {pkg.price}
        </div>
      </div>
      {pkg.includes && pkg.includes.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-white/80 mb-2">Includes:</p>
          <div className="flex flex-wrap gap-1">
            {pkg.includes.slice(0, 3).map((item, index) => (
              <Badge key={index} variant="secondary" className="text-xs bg-white/20 text-white">
                {item}
              </Badge>
            ))}
            {pkg.includes.length > 3 && (
              <Badge variant="secondary" className="text-xs bg-white/20 text-white">
                +{pkg.includes.length - 3} more
              </Badge>
            )}
          </div>
        </div>
      )}
      <Button onClick={onBookNow} className="bg-travel-primary text-white hover:bg-travel-primary/90 font-semibold">
        Book This Package
      </Button>
    </div>
  </div>
);

const AccommodationCard = ({ accommodation, onBookNow }: { accommodation: FeaturedAccommodation; onBookNow: () => void }) => (
  <div className="grid md:grid-cols-2 gap-6 items-center">
    <div className="relative overflow-hidden rounded-lg">
      <img
        src={accommodation.image_url}
        alt={accommodation.name}
        className="w-full h-48 object-cover transition-transform hover:scale-105"
      />
      <Badge className="absolute top-2 left-2 bg-travel-primary text-white">
        Featured Stay
      </Badge>
      <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
        <Star className="w-3 h-3 text-yellow-400 fill-current" />
        <span className="text-white text-xs font-medium">{accommodation.rating}</span>
      </div>
    </div>
    <div className="text-white">
      <h3 className="text-2xl font-bold mb-2">{accommodation.name}</h3>
      <p className="text-white/80 mb-4 line-clamp-2">{accommodation.description}</p>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">{accommodation.location}</span>
        </div>
        <div className="text-xl font-bold text-travel-secondary">
          {accommodation.price_range}
        </div>
      </div>
      {accommodation.amenities && accommodation.amenities.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-white/80 mb-2">Amenities:</p>
          <div className="flex flex-wrap gap-1">
            {accommodation.amenities.slice(0, 3).map((amenity, index) => (
              <Badge key={index} variant="secondary" className="text-xs bg-white/20 text-white">
                {amenity}
              </Badge>
            ))}
            {accommodation.amenities.length > 3 && (
              <Badge variant="secondary" className="text-xs bg-white/20 text-white">
                +{accommodation.amenities.length - 3} more
              </Badge>
            )}
          </div>
        </div>
      )}
      <Button onClick={onBookNow} className="bg-travel-primary text-white hover:bg-travel-primary/90 font-semibold">
        Book This Stay
      </Button>
    </div>
  </div>
);